import React, { useState } from 'react';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { getApiUrl } from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: "მისამართი",
      details: [
        "ხაშური 5700",
        "წმინდა ნინოს N1"
      ]
    },
    {
      icon: PhoneIcon,
      title: "ტელეფონი",
      details: [
        "(368) 24 00 54",
        "(368) 24 00 74"
      ]
    },
    {
      icon: EnvelopeIcon,
      title: "ელ. ფოსტა",
      details: [
        "info@fciveria.ge",
        "social@fciveria.ge",
        "tickets@fciveria.ge"
      ]
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(getApiUrl('/api/contact/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(result.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        alert(result.error || 'შეცდომა შეტყობინების გაგზავნისას. გთხოვთ სცადოთ მოგვიანებით.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('შეცდომა შეტყობინების გაგზავნისას. გთხოვთ სცადოთ მოგვიანებით.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            კონტაქტი
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            დაგვიკავშირდით ნებისმიერი კითხვისთვის ან წინადადებისთვის
          </p>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {contactInfo.map((info, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <info.icon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-4">
                {info.title}
              </h3>
              <div className="space-y-2">
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-gray-600 font-georgian text-sm">
                    {detail}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Staff Contacts */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 font-georgian mb-8 text-center">
            ჩვენი გუნდი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* ვანო ხარაზიშვილი - დირექტორი */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <PhoneIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                ვანო ხარაზიშვილი
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">დირექტორი</p>
              <div className="space-y-2">
                <a 
                  href="tel:591992225" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                  +995 591 99 22 25
                </a>
                <a 
                  href="mailto:director@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  director@fciveria.ge
                </a>
              </div>
            </div>

            {/* გია აბელიანი - გულშემატკივრების და შშმ პირების */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <PhoneIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                გია აბელიანი
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">გულშემატკივრების და შშმ პირების მომსახურება</p>
              <div className="space-y-2">
                <a 
                  href="tel:557909826" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                  +995 557 90 98 26
                </a>
                <a 
                  href="mailto:fans@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  fans@fciveria.ge
                </a>
              </div>
            </div>

            {/* პაატა ქიმერიძე - სოციალური პასუხისმგებლობის ოფიცერი */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <PhoneIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                პაატა ქიმერიძე
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">სოციალური პასუხისმგებლობის ოფიცერი</p>
              <div className="space-y-2">
                <a 
                  href="tel:599747010" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                  +995 599 74 70 10
                </a>
                <a 
                  href="mailto:social@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  social@fciveria.ge
                </a>
              </div>
            </div>

            {/* ვახტანგ ჯაფარაშვილი - მატჩების მენეჯერი */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <PhoneIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                ვახტანგ ჯაფარაშვილი
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">მატჩების მენეჯერი</p>
              <div className="space-y-2">
                <a 
                  href="tel:+995551551185" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                  +995 551 55 11 85
                </a>
                <a 
                  href="mailto:matches@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  matches@fciveria.ge
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
              დაგვიკავშირდით
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სახელი
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                    placeholder="შეიყვანეთ თქვენი სახელი"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    გვარი
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                    placeholder="შეიყვანეთ თქვენი გვარი"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ელ. ფოსტა
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  placeholder="example@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ტელეფონი
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  placeholder="+995 599 123 456"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  თემა
                </label>
                <select 
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                >
                  <option value="">აირჩიეთ თემა</option>
                  <option value="ზოგადი ინფორმაცია">ზოგადი ინფორმაცია</option>
                  <option value="ბილეთები">ბილეთები</option>
                  <option value="ახალგაზრდული აკადემია">ახალგაზრდული აკადემია</option>
                  <option value="მერჩენდაიზი">მერჩენდაიზი</option>
                  <option value="სხვა">სხვა</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  შეტყობინება
                </label>
                <textarea
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  placeholder="შეიყვანეთ თქვენი შეტყობინება"
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary-600 hover:bg-primary-700'
                } text-white`}
              >
                {isSubmitting ? 'გაგზავნა...' : 'შეტყობინების გაგზავნა'}
              </button>
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            ჩვენი მდებარეობა
          </h2>
          <div className="text-center mb-4">
            <p className="text-gray-600 font-georgian">
              ხაშური 5700, წმინდა ნინოს N1
            </p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-md">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d679.7423290668671!2d43.5944058531062!3d41.99905086151564!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4043502280eeca8d%3A0x610495e08026c861!2zS2hhc2h1cmkgU3RhZGl1bSDigJxJdmVyaWHigJ0!5e0!3m2!1sen!2sge!4v1758046120807!5m2!1sen!2sge" 
              width="100%" 
              height="450" 
              style={{border: 0}} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="FC Iveria Location Map"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
